import { format } from 'date-fns';
import { useQuery } from 'react-query';
import useFetchUserDetails from './useFetchUserDetails';
import useFetchCompensationDetails from './useFetchCompensationDetails';
import { AppError } from 'utils/AppError';
import { FetchCompensationDetailsResponse } from './useFetchCompensationDetails';
import api from 'api';

export type PayDetailsData = {
  userData: any;
  compensationData?: FetchCompensationDetailsResponse;
  firstName: string | null;
  error: AppError | null;
  isLoading: boolean;
  refetch: () => void;
};

const useFetchPayDetailsData = (userId: string | null): PayDetailsData => {
  const {
    data: userData,
    firstName,
    error: userError,
    isLoading: isUserDataLoading,
    refetch: refetchUserData,
  } = useFetchUserDetails(userId);

  // Fetch salary details to get ecssProfileDisplayMonth — used for mid-month ECSS revisions
  // so the CTC component breakdown shows old/new values, never prorated values.
  const {
    data: salaryDetailsData,
    isLoading: isSalaryDetailsLoading,
    refetch: refetchSalaryDetails,
  } = useQuery(
    ['payDetailsSalaryDetails', userId],
    () => api.userQueries.getSalaryDetails({ userId: userId! }),
    { enabled: !!userId },
  );

  const deactivateDate = userData?.deactivateDate
    ? format(new Date(userData.deactivateDate ?? ''), 'yyyy-MM-dd')
    : null;
  const currentDate = format(Date.now(), 'yyyy-MM-dd');
  const basePayrollMonth = deactivateDate
    ? deactivateDate >= currentDate
      ? deactivateDate
      : currentDate
    : currentDate;

  // For mid-month ECSS revisions: use the display month from the backend so PC returns
  // old (before effectiveFrom) or real ECSS (after effectiveFrom) values — never prorated.
  const ecssProfileDisplayMonth =
    (salaryDetailsData as any)?.payrollGetSalaryDetails?.data?.ecssProfileDisplayMonth ?? null;
  const payrollMonth = ecssProfileDisplayMonth ?? basePayrollMonth;

  const {
    data: compensationData,
    isLoading: isCompensationDataLoading,
    error: compensationError,
    refetch: refetchCompensationData,
  } = useFetchCompensationDetails({ userId, payrollMonth });

  const isLoading = isUserDataLoading || isSalaryDetailsLoading || isCompensationDataLoading;
  const error = userError || compensationError;

  const refetch = () => {
    refetchUserData();
    refetchSalaryDetails();
    refetchCompensationData();
  };

  return {
    userData,
    compensationData,
    firstName,
    error,
    isLoading,
    refetch,
  };
};

export default useFetchPayDetailsData;
